/**
 * Music Metrics Hook
 * Fetch and manage music adjustment telemetry
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export interface MusicAdjustmentStats {
  total_adjustments: number;
  successful_adjustments: number;
  success_rate_percent: number;
  avg_bpm_difference: number;
  avg_adjustment_duration_ms: number;
  avg_tracks_found: number;
  adjustments_by_provider: Record<string, number>;
  adjustments_by_day: Array<{
    date: string;
    count: number;
    success_count: number;
    avg_bpm: number;
  }>;
}

export interface MusicMetric {
  id: string;
  user_id: string;
  run_id?: string;
  previous_bpm: number;
  target_bpm: number;
  bpm_difference: number;
  avg_session_bpm?: number;
  adjustment_successful: boolean;
  tracks_found: number;
  adjustment_duration_ms?: number;
  provider_type: string;
  trigger_reason?: string;
  user_zone?: string;
  created_at: string;
}

/**
 * Record a music adjustment metric
 */
export async function recordMusicAdjustment(metric: Omit<MusicMetric, 'id' | 'user_id' | 'created_at'>) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.warn('Cannot record music metric: user not authenticated');
      return;
    }

    const { error } = await supabase
      .from('music_adjustment_metrics')
      .insert({
        user_id: user.id,
        ...metric
      });

    if (error) {
      logger.error('Failed to record music adjustment metric', error);
      return;
    }

    logger.debug('Music adjustment metric recorded', {
      target_bpm: metric.target_bpm,
      successful: metric.adjustment_successful,
      provider: metric.provider_type
    });
  } catch (error) {
    logger.error('Error recording music metric', error);
  }
}

/**
 * Hook to fetch music adjustment statistics
 */
export function useMusicMetrics(days: number = 30) {
  return useQuery({
    queryKey: ['music-metrics', days],
    queryFn: async (): Promise<MusicAdjustmentStats | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase.rpc('get_music_adjustment_stats', {
        p_user_id: user.id,
        p_days: days
      });

      if (error) {
        logger.error('Failed to fetch music metrics', error);
        throw error;
      }

      return data as unknown as MusicAdjustmentStats;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true
  });
}

/**
 * Hook to fetch recent music adjustment events
 */
export function useRecentMusicAdjustments(limit: number = 20) {
  return useQuery({
    queryKey: ['recent-music-adjustments', limit],
    queryFn: async (): Promise<MusicMetric[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('music_adjustment_metrics')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.error('Failed to fetch recent music adjustments', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: true
  });
}
